/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-physical-properties */
import { useEffect, useMemo, useRef } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';

import { useEnvironmentInfo } from '../../../utils/wae-context-provider';

import { getMarketoFormLocalization } from './marketo-form-messages';
import { getMarketoConfig, MarketoFormIds, useMarketoForm } from './use-marketo-form';

export type UniformMarketoFormProps = MarketoFormIds & {
	label?: string;
	successMessageOverride?: string;
	errorMessageOverride?: string;
};

export const DEFAULT_MARKETO_FORM_ANALYTICS_LABEL = 'Marketo form';

const marketoErrorIconBackground =
	"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='12' r='12' fill='%23AE2A19'/%3E%3Cpath d='M12 5.75v8' stroke='%23FFFFFF' stroke-width='2.25' stroke-linecap='round'/%3E%3Ccircle cx='12' cy='17.75' r='1.35' fill='%23FFFFFF'/%3E%3C/svg%3E\")";

const marketoFormWrapperCss = css({
	width: '100%',
	'--wac-color-heading': wacToken('color.primary.black'),
	'--wac-color-body': wacToken('color.primary.black'),
});

const marketoFormContainerCss = css({
	width: '100%',
	color: wacToken('color.primary.black'),
	fontFamily: wacToken('font.charlie.text'),
	'& .mktoForm': {
		boxSizing: 'border-box',
		maxWidth: '100%',
		overflow: 'visible',
		width: '100% !important',
		color: wacToken('color.primary.black'),
		fontFamily: wacToken('font.charlie.text'),
	},
	'& .mktoFormRow': {
		display: 'block',
		margin: `0 0 ${token('space.200')}`,
		overflow: 'visible',
		width: '100%',
	},
	'& .mktoFormRow:last-of-type': {
		marginBlockEnd: 0,
	},
	'& .mktoFormCol, & .mktoFieldDescriptor, & .mktoFieldWrap': {
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		float: 'none !important',
		margin: '0 !important',
		minHeight: '0 !important',
		overflow: 'visible',
		width: '100% !important',
	},
	'& .mktoLabel': {
		alignSelf: 'flex-start',
		boxSizing: 'border-box',
		color: `${wacToken('color.primary.black')} !important`,
		display: 'block',
		float: 'none !important',
		fontFamily: `${wacToken('font.charlie.text')} !important`,
		fontSize: '0.875rem',
		fontWeight: 500,
		lineHeight: '1.25rem',
		margin: `0 0 ${token('space.050')}`,
		padding: '0 !important',
		width: '100% !important',
	},
	'& .mktoForm input[type="text"], & .mktoForm input[type="email"], & .mktoForm input[type="tel"], & .mktoForm input[type="url"], & .mktoForm select, & .mktoForm textarea':
		{
			appearance: 'none',
			backgroundColor: `${wacToken('color.neutral.light0')} !important`,
			border: `1px solid ${wacToken('color.neutral.light600')} !important`,
			borderRadius: wacToken('corner.radius.card'),
			boxSizing: 'border-box',
			color: `${wacToken('color.primary.black')} !important`,
			display: 'block',
			fontFamily: `${wacToken('font.charlie.text')} !important`,
			fontWeight: 400,
			margin: '0 !important',
			maxWidth: '100%',
			minHeight: token('space.500'),
			minWidth: 0,
			padding: `0 ${token('space.150')} !important`,
			width: '100% !important',
		},
	'& .mktoForm textarea': {
		minHeight: token('space.1000'),
		paddingBlock: `${token('space.100')} !important`,
		resize: 'vertical',
	},
	'& .mktoForm select': {
		backgroundImage:
			"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='9' viewBox='0 0 14 9' fill='none'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M0.282354 2.04788C-0.0941681 1.65992 -0.0941683 1.04295 0.282353 0.654992L0.623427 0.303554C1.01623 -0.101184 1.66584 -0.101184 2.05864 0.303554L6.60645 4.98954L11.1542 0.303554C11.547 -0.101184 12.1967 -0.101184 12.5895 0.303554L12.9305 0.654992C13.3071 1.04295 13.3071 1.65992 12.9305 2.04788L7.32405 7.82473C6.93125 8.22946 6.28164 8.22946 5.88884 7.82473L0.282354 2.04788Z' fill='%23101214'/%3E%3C/svg%3E\")",
		backgroundPosition: `right ${token('space.150')} center`,
		backgroundRepeat: 'no-repeat',
		backgroundSize: '14px 9px',
		paddingInlineEnd: `${token('space.400')} !important`,
	},
	'& .mktoForm input::placeholder, & .mktoForm textarea::placeholder': {
		color: `${wacToken('color.neutral.light600')} !important`,
		fontWeight: 400,
		opacity: 1,
	},
	'& .mktoForm input:focus, & .mktoForm select:focus, & .mktoForm textarea:focus': {
		borderColor: `${wacToken('color.primary.blue')} !important`,
		boxShadow: `inset 0 0 0 1px ${wacToken('color.primary.blue')}`,
		outline: 'none',
	},
	'& .mktoInstruction, & .mktoHtmlText': {
		color: `${wacToken('color.neutral.light600')} !important`,
		fontFamily: `${wacToken('font.charlie.text')} !important`,
		fontSize: '0.75rem',
		fontWeight: 400,
		lineHeight: '1.125rem',
		marginBlockStart: token('space.050'),
	},
	'& .mktoHtmlText': {
		width: '100% !important',
	},
	'& .mktoInstruction a, & .mktoHtmlText a': {
		color: `${wacToken('color.primary.blue')} !important`,
		textDecoration: 'underline',
	},
	'& .mktoCheckboxList': {
		alignItems: 'flex-start',
		columnGap: token('space.100'),
		display: 'grid',
		gridTemplateColumns: '18px minmax(0, 1fr)',
		marginBlockStart: token('space.050'),
		rowGap: token('space.100'),
		width: '100% !important',
	},
	'& .mktoCheckboxList input[type="checkbox"]': {
		appearance: 'none',
		backgroundColor: `${wacToken('color.neutral.light0')} !important`,
		backgroundPosition: 'center',
		backgroundRepeat: 'no-repeat',
		backgroundSize: '12px 12px',
		border: `${token('space.025')} solid ${wacToken('color.primary.black')} !important`,
		borderRadius: token('space.050'),
		boxSizing: 'border-box',
		cursor: 'pointer',
		display: 'inline-grid',
		flex: '0 0 18px',
		height: '18px',
		margin: `${token('space.050')} 0 0 !important`,
		minHeight: '18px',
		placeContent: 'center',
		width: '18px',
	},
	'& .mktoCheckboxList input[type="checkbox"]:hover': {
		borderColor: `${wacToken('color.primary.blue')} !important`,
	},
	'& .mktoCheckboxList input[type="checkbox"]:focus': {
		borderColor: `${wacToken('color.primary.blue')} !important`,
		boxShadow: 'none',
		outline: 'none',
	},
	'& .mktoCheckboxList input[type="checkbox"]:focus-visible': {
		outline: `${token('space.025')} solid ${wacToken('color.primary.blue')}`,
		outlineOffset: token('space.025'),
	},
	'& .mktoCheckboxList input[type="checkbox"]:checked': {
		backgroundColor: `${wacToken('color.primary.blue')} !important`,
		backgroundImage:
			"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M2.5 6.1 5 8.6l4.5-5.2' fill='none' stroke='%23FFFFFF' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
		borderColor: `${wacToken('color.primary.blue')} !important`,
	},
	'& .mktoCheckboxList label': {
		alignItems: 'flex-start',
		color: wacToken('color.primary.black'),
		cursor: 'pointer',
		display: 'inline-flex',
		minHeight: '18px',
	},
	'& .mktoCheckboxList .mktoLabel': {
		alignSelf: 'flex-start',
		display: 'block',
		fontFamily: `${wacToken('font.charlie.text')} !important`,
		fontSize: '1rem',
		fontWeight: 400,
		lineHeight: '1.5rem',
		margin: '0 !important',
		width: '100% !important',
	},
	'& .mktoRadioList': {
		alignItems: 'center',
		columnGap: token('space.100'),
		display: 'grid',
		gridTemplateColumns: '20px minmax(0, 1fr)',
		marginBlockStart: token('space.050'),
		rowGap: token('space.100'),
		width: '100% !important',
	},
	'& .mktoRadioList input[type="radio"]': {
		appearance: 'none',
		backgroundColor: `${wacToken('color.neutral.light0')} !important`,
		border: `${token('space.025')} solid ${wacToken('color.primary.black')} !important`,
		borderRadius: '50%',
		boxSizing: 'border-box',
		cursor: 'pointer',
		display: 'inline-grid',
		height: '20px',
		margin: '0 !important',
		minHeight: '20px',
		placeContent: 'center',
		width: '20px',
	},
	'& .mktoRadioList input[type="radio"]::before': {
		backgroundColor: wacToken('color.primary.blue'),
		borderRadius: '50%',
		content: '""',
		display: 'block',
		height: '10px',
		transform: 'scale(0)',
		width: '10px',
	},
	'& .mktoRadioList input[type="radio"]:hover': {
		borderColor: `${wacToken('color.primary.blue')} !important`,
	},
	'& .mktoRadioList input[type="radio"]:focus': {
		borderColor: `${wacToken('color.primary.blue')} !important`,
		boxShadow: 'none',
		outline: 'none',
	},
	'& .mktoRadioList input[type="radio"]:focus-visible': {
		outline: `${token('space.025')} solid ${wacToken('color.primary.blue')}`,
		outlineOffset: token('space.025'),
	},
	'& .mktoRadioList input[type="radio"]:checked': {
		borderColor: `${wacToken('color.primary.blue')} !important`,
	},
	'& .mktoRadioList input[type="radio"]:checked::before': {
		transform: 'scale(1)',
	},
	'& .mktoRadioList input[type="radio"]:disabled': {
		borderColor: `${wacToken('color.neutral.light500')} !important`,
		cursor: 'not-allowed',
	},
	'& .mktoRadioList input[type="radio"]:disabled::before': {
		backgroundColor: wacToken('color.neutral.light500'),
	},
	'& .mktoRadioList input[type="radio"]:disabled + label': {
		color: wacToken('color.neutral.light500'),
		cursor: 'not-allowed',
	},
	'& .mktoRadioList label': {
		color: wacToken('color.primary.black'),
		alignItems: 'center',
		cursor: 'pointer',
		display: 'inline-flex',
		minHeight: '20px',
	},
	'& .mktoButtonRow': {
		display: 'block',
		marginBlockStart: token('space.200'),
		width: '100%',
	},
	'& .mktoButtonRow, & .mktoButtonWrap': {
		marginInlineStart: '0 !important',
		marginLeft: '0 !important',
	},
	'& .mktoButtonWrap': {
		display: 'block',
		width: '100%',
	},
	'& .mktoButton': {
		alignItems: 'center',
		backgroundColor: `${wacToken('color.primary.blue')} !important`,
		border: '2px solid transparent !important',
		borderRadius: wacToken('corner.radius.pill'),
		boxSizing: 'border-box',
		color: `${wacToken('color.neutral.light0')} !important`,
		cursor: 'pointer',
		display: 'inline-flex',
		fontFamily: `${wacToken('font.charlie.text')} !important`,
		fontSize: '1.25rem',
		fontWeight: 500,
		justifyContent: 'center',
		lineHeight: '1.5rem',
		maxWidth: '368px',
		minHeight: '40px',
		minWidth: '120px',
		padding: `${token('space.075')} 22px ${token('space.100')} !important`,
		textAlign: 'center',
		textDecoration: 'none',
		transition: `scale ${wacToken('motion.duration.150ms')} ${wacToken(
			'motion.easing.ease40-in-out',
		)}, background-color ${wacToken('motion.duration.150ms')} ${wacToken(
			'motion.easing.ease40-in-out',
		)}`,
	},
	'& .mktoButton:hover': {
		backgroundColor: `${wacToken('color.extended.blue900')} !important`,
	},
	'& .mktoButton:focus': {
		outline: 'none',
	},
	'& .mktoButton:focus-visible': {
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: token('space.050'),
	},
	'& .mktoButton:active': {
		scale: '97%',
	},
	'& .mktoButton:disabled, & .mktoButton[disabled]': {
		backgroundColor: `${wacToken('color.neutral.light400')} !important`,
		borderColor: 'transparent !important',
		color: `${wacToken('color.neutral.light0')} !important`,
		cursor: 'not-allowed',
		scale: 'none',
	},
	'& .mktoButton:disabled:hover, & .mktoButton[disabled]:hover': {
		backgroundColor: `${wacToken('color.neutral.light400')} !important`,
	},
	'& .mktoAsterix': {
		color: wacToken('color.secondary.red'),
		display: 'inline',
		paddingInlineStart: token('space.025'),
		verticalAlign: 'baseline',
	},
	'& .mktoError': {
		insetBlockEnd: 'auto !important',
		insetInlineEnd: 'auto !important',
		marginBlockStart: token('space.050'),
		position: 'relative !important',
		right: 'auto !important',
		bottom: 'auto !important',
	},
	'& .mktoErrorMsg': {
		backgroundColor: 'transparent !important',
		backgroundImage: 'none !important',
		border: 'none !important',
		borderRadius: 0,
		boxShadow: 'none !important',
		color: `${wacToken('color.secondary.red')} !important`,
		fontFamily: `${wacToken('font.charlie.text')} !important`,
		fontSize: '0.75rem',
		fontWeight: 400,
		lineHeight: '1.125rem',
		margin: '0 !important',
		padding: '0 !important',
		textShadow: 'none !important',
	},
	'& .mktoErrorArrowWrap': {
		display: 'none !important',
	},
	'& .mktoForm input.mktoInvalid, & .mktoForm select.mktoInvalid, & .mktoForm textarea.mktoInvalid':
		{
			borderColor: `${wacToken('color.secondary.red')} !important`,
			boxShadow: `inset 0 0 0 1px ${wacToken('color.secondary.red')}`,
		},
	'& .mktoForm input[type="text"].mktoInvalid:not(:focus), & .mktoForm input[type="email"].mktoInvalid:not(:focus), & .mktoForm input[type="tel"].mktoInvalid:not(:focus), & .mktoForm input[type="url"].mktoInvalid:not(:focus)':
		{
			backgroundImage: marketoErrorIconBackground,
			backgroundPosition: `right ${token('space.100')} center`,
			backgroundRepeat: 'no-repeat',
			backgroundSize: '24px 24px',
			paddingInlineEnd: `${token('space.800')} !important`,
		},
	'& .mktoForm textarea.mktoInvalid:not(:focus)': {
		backgroundImage: marketoErrorIconBackground,
		backgroundPosition: `right ${token('space.100')} top ${token('space.100')}`,
		backgroundRepeat: 'no-repeat',
		backgroundSize: '24px 24px',
		paddingInlineEnd: `${token('space.800')} !important`,
	},
});

const messageCss = css({
	color: wacToken('color.primary.black'),
	margin: `${token('space.200')} 0 0`,
});

const UniformMarketoForm = ({
	label,
	successMessageOverride,
	errorMessageOverride,
	...ids
}: UniformMarketoFormProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const hasFiredTerminalAnalyticsRef = useRef(false);
	const intl = useIntl();
	const { isProduction } = useEnvironmentInfo();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const marketoConfig = useMemo(
		() => getMarketoConfig(ids, isProduction),
		[ids.formId, ids.formIdStg, isProduction],
	);
	const marketoLocalization = useMemo(() => getMarketoFormLocalization(intl), [intl]);
	const status = useMarketoForm({
		containerRef,
		config: marketoConfig,
		localization: marketoLocalization,
	});
	const analyticsLabel = label?.trim() || DEFAULT_MARKETO_FORM_ANALYTICS_LABEL;

	const successMessage =
		successMessageOverride?.trim() ||
		intl.formatMessage({
			id: 'uniform.marketoForm.successMessage',
			defaultMessage: 'Thanks! Someone will reach out to you shortly.',
		});
	const errorMessage =
		errorMessageOverride?.trim() ||
		intl.formatMessage({
			id: 'uniform.marketoForm.errorMessage',
			defaultMessage: 'An error occurred while processing this request. Please try again.',
		});

	useEffect(() => {
		if (status !== 'success' && status !== 'error') {
			hasFiredTerminalAnalyticsRef.current = false;
			return;
		}

		if (hasFiredTerminalAnalyticsRef.current) {
			return;
		}

		hasFiredTerminalAnalyticsRef.current = true;
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'submitted',
			actionSubject: 'form',
			actionSubjectId: 'marketoForm',
			source: 'uniform',
			attributes: {
				label: analyticsLabel,
				status,
				formId: marketoConfig.formId,
				orgId: marketoConfig.orgId,
			},
		}).fire();
	}, [analyticsLabel, createAnalyticsEvent, marketoConfig.formId, marketoConfig.orgId, status]);

	const shouldShowFormContainer = status === 'loading' || status === 'form';

	return (
		<TypographyStyles>
			<div css={marketoFormWrapperCss} data-testid="uniform-marketo-form">
				{shouldShowFormContainer && (
					<div
						aria-busy={status === 'loading'}
						css={marketoFormContainerCss}
						data-testid="uniform-marketo-form-container"
						ref={containerRef}
					/>
				)}
				{status === 'success' && (
					<p className="body-md" css={messageCss} role="status">
						{successMessage}
					</p>
				)}
				{status === 'error' && (
					<p className="body-md" css={messageCss} role="alert">
						{errorMessage}
					</p>
				)}
			</div>
		</TypographyStyles>
	);
};

export default UniformMarketoForm;
